import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import type { Feature } from 'geojson';
import type { SearchResultItem } from '../../compound-components/model';
import { GeoService } from '../../viewer-components/services/internal-services/geo.service';
import { SearchUtilsService } from '../../compound-components/services/search/utils/search-utils.service';

export interface PipelineEmbeddingsRegisteredLayer {
  layerName: string;
  features: Feature[];
}

/**
 * Converts a pipeline embeddings GeoJSON feature (already pre-enriched with score/scoreColor/centroid/geometryWkt)
 * into a SearchResultItem so search-result-card components can render it.
 */
export function featureToEmbeddingSearchItem(f: Feature): SearchResultItem {
  const p: any = f.properties ?? {};
  return {
    key: String((f as any).id ?? p.quadkey ?? crypto.randomUUID()),
    quadkey: p.quadkey ?? '',
    similarity: p.similarity ?? p.score ?? 0,
    geometry: p.geometryWkt ?? GeoService.geoJSONFeatureToWKT(f.geometry) ?? '',
    centroid: p.centroid ?? null,
    score: p.score ?? 0,
    scoreColor: p.scoreColor ?? SearchUtilsService.similarityColor(p.score ?? 0),
    collection: p.collection ?? undefined,
  };
}

/**
 * Manages the pipeline embeddings floating panel and individual feature detail dialogs.
 * Mirrors VlmLayerPanelService — completely independent of SearchQueryService.
 */
@Injectable({ providedIn: 'root' })
export class PipelineEmbeddingsPanelService {
  private readonly _registeredLayer = signal<PipelineEmbeddingsRegisteredLayer | null>(null);
  private readonly _panelOpen = signal<boolean>(false);

  readonly registeredLayer = this._registeredLayer.asReadonly();
  readonly panelOpen = this._panelOpen.asReadonly();

  /** Emits when a single embedding feature should be shown in a detail dialog. */
  readonly featureDetailTrigger$ = new Subject<Feature>();

  /** Stores the layer data so the panel can display it. */
  register(layer: PipelineEmbeddingsRegisteredLayer): void {
    this._registeredLayer.set(layer);
  }

  /** Opens the floating panel. Requires register() to have been called first. */
  open(): void {
    this._panelOpen.set(true);
  }

  /** Closes the floating panel. */
  close(): void {
    this._panelOpen.set(false);
  }

  /** Triggers a detail dialog for the given feature without opening the full panel. */
  openFeatureDetail(feature: Feature): void {
    this.featureDetailTrigger$.next(feature);
  }
}
